import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import { PdfButton } from "../components/ListingPdf";
import { RefPrice } from "../lib/price";
import { ListingMap } from "../components/ListingMap";
import { StayFields } from "../components/StayFields";
import { MapPin, Users, Calendar as CalIcon, Check, ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  const v = Number(n);
  const d = Number.isInteger(v) ? 0 : 2;
  return `${v.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d })} €`;
};

function PriceGrid({ l }) {
  const rows = [
    ["1 mois", l.price_1m_month, l.price_1m_night],
    ["3 mois", l.price_3m_month, l.price_3m_night],
  ].filter(([, m, n]) => m != null || n != null);
  if (!rows.length) return null;
  return (
    <table className="mt-5 w-full text-sm border-t border-[#E5E1D8]" data-testid="listing-price-grid">
      <thead>
        <tr className="text-xs tracking-[0.15em] uppercase text-[#666]">
          <th className="text-left font-semibold py-2">Séjour</th>
          <th className="text-right font-semibold py-2">Par mois</th>
          <th className="text-right font-semibold py-2">Par nuit</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, m, n]) => (
          <tr key={label} className="border-t border-[#F0EDE6]">
            <td className="py-2 text-[#2B3029]">{label}</td>
            <td className="py-2 text-right font-medium text-[#1A1A1A]">{m != null ? fmt(m) : "—"}</td>
            <td className="py-2 text-right font-medium text-[#1A1A1A]">{n != null ? fmt(n) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ arrival_date: "", departure_date: "", guests: 1, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/listings/${id}`, { withCredentials: true });
        setListing(data);
      } catch {
        toast.error("Annonce introuvable");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reservations`,
        { listing_id: id, ...form, guests: Number(form.guests) },
        { withCredentials: true }
      );
      toast.success("Demande envoyée ! Le gestionnaire vous recontactera.");
      setForm({ arrival_date: "", departure_date: "", guests: 1, message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F5F3ED]"><NavBar /><div className="p-10 text-sm text-[#666]">Chargement…</div></div>;
  if (!listing) return null;

  const images = listing.image_urls || [];
  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setLightbox((i) => (i === images.length - 1 ? 0 : i + 1));

  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <NavBar />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8">
        <Link to="/catalog" className="text-sm text-[#666] hover:text-[#8C6C50] inline-flex items-center gap-1" data-testid="back-to-catalog">
          <ArrowLeft className="w-4 h-4" /> Retour au catalogue
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          {listing.brand && (
            <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50]" data-testid="listing-brand">{listing.brand}</div>
          )}
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-3 text-[#1A1A1A]">
            {listing.title}
          </h1>
          {listing.residence_name && (
            <div className="mt-2 text-base text-[#2B3029]" data-testid="listing-residence-name">{listing.residence_name}</div>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-[#666]">
            <span className="flex items-center gap-1.5" data-testid="listing-address"><MapPin className="w-4 h-4" /> {[listing.address, [listing.postal_code, listing.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {listing.capacity} personne{listing.capacity > 1 ? "s" : ""}</span>
            <span className="flex items-center gap-1.5"><CalIcon className="w-4 h-4" /> dès {listing.min_duration_months} mois</span>
            <PdfButton id={listing.id} testid="listing-pdf-btn" />
          </div>

          {/* Gallery */}
          {images.length > 0 && (
            <div className="mt-8" data-testid="gallery">
              {images.length === 1 ? (
                <button onClick={() => openLightbox(0)} className="block aspect-[16/10] overflow-hidden w-full">
                  <img src={images[0]} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform" />
                </button>
              ) : (
                <div className="grid grid-cols-4 grid-rows-2 gap-2 aspect-[16/10]">
                  <button onClick={() => openLightbox(0)} className="col-span-2 row-span-2 overflow-hidden">
                    <img src={images[0]} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform" />
                  </button>
                  {images.slice(1, 5).map((u, i) => (
                    <button key={i} onClick={() => openLightbox(i + 1)} className="overflow-hidden relative">
                      <img src={u} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform" />
                      {i === 3 && images.length > 5 && (
                        <span className="absolute inset-0 bg-black/50 text-white text-sm font-medium flex items-center justify-center">
                          +{images.length - 5} photos
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-10">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">À propos de ce logement</h2>
            <p className="mt-4 text-base text-[#2B3029] leading-relaxed whitespace-pre-line">{listing.description}</p>
          </div>

          {listing.amenities?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl text-[#1A1A1A]">Services & équipements</h2>
              <ul className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {listing.amenities.map((a) => (
                  <li key={a} className="flex items-center gap-2 text-sm text-[#2B3029]">
                    <Check className="w-4 h-4 text-[#8C6C50]" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(listing.neighborhood_ambiance || listing.neighborhood_shops || listing.neighborhood_transport) && (
            <div className="mt-10" data-testid="listing-neighborhood">
              <h2 className="font-serif text-2xl text-[#1A1A1A]">Le quartier</h2>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                {[["Ambiance", listing.neighborhood_ambiance], ["Commerces", listing.neighborhood_shops], ["Transports", listing.neighborhood_transport]]
                  .filter(([, t]) => t)
                  .map(([k, t]) => (
                    <div key={k} className="border-t border-[#E5E1D8] pt-4">
                      <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8C6C50]">{k}</div>
                      <p className="mt-2 text-sm text-[#2B3029] leading-relaxed">{t}</p>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <ListingMap listing={listing} />
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-28 bg-white border border-[#E5E1D8] p-8 rounded-md" data-testid="reservation-panel">
            <div className="flex items-baseline justify-between">
              <RefPrice l={listing} big="text-4xl" testid="listing-ref-price" />
            </div>
            <PriceGrid l={listing} />
            <form onSubmit={submit} className="mt-6 space-y-4">
              <StayFields form={form} setForm={setForm} listings={[listing]} minMonths={listing.min_duration_months || 1} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#666]">Personnes</label>
                  <input
                    type="number"
                    required
                    min={1}
                    max={listing.capacity}
                    value={form.guests}
                    onChange={(e) => setForm({ ...form, guests: e.target.value })}
                    className="w-full mt-2 px-4 py-3 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md focus:outline-none focus:border-[#8C6C50]"
                    data-testid="reservation-guests"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#666]">Message (optionnel)</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full mt-2 px-4 py-3 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md focus:outline-none focus:border-[#8C6C50]"
                  placeholder="Précisez votre contexte, flexibilité, besoins particuliers…"
                  data-testid="reservation-message"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full py-3.5 rounded-full text-sm font-medium disabled:opacity-60"
                data-testid="reservation-submit-btn"
              >
                {submitting ? "Envoi…" : "Envoyer ma demande"}
              </button>
              <p className="text-xs text-[#666] mt-2">
                Votre demande est envoyée directement à la résidence et suivie par notre équipe.
              </p>
            </form>
          </div>
        </div>
      </section>

      {lightbox !== null && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" onClick={closeLightbox}>
          <button className="absolute top-5 right-5 text-white p-2" onClick={closeLightbox} data-testid="lightbox-close">
            <X className="w-7 h-7" />
          </button>
          <button className="absolute left-5 text-white p-3" onClick={(e) => { e.stopPropagation(); prev(); }}>
            <ChevronLeft className="w-8 h-8" />
          </button>
          <img
            src={images[lightbox]}
            alt=""
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button className="absolute right-5 text-white p-3" onClick={(e) => { e.stopPropagation(); next(); }}>
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="absolute bottom-6 text-white text-sm">{lightbox + 1} / {images.length}</div>
        </div>
      )}
    </div>
  );
}
